/**
 * 計算量メーターの比較実験 3 本（docs/curriculum/labs.md「最初の 3 つの比較実験」）。
 * 遅い書き方を先に実行し、予想した予想と実測を並べる。
 *
 * 入力は complexity-meter.md §3 の `n-only`（stdin は "{N}\n"）。
 * データはプログラムの中で作る（labs.md の共通の入力生成規則。seed を省いたときは 1）。
 */
import {
  evalOps,
  formatCount,
  groupDigits,
  type MeterPoint,
  type MeterSpec,
} from '../../lib/complexity';

export type ExperimentId = 'duplicate' | 'search' | 'range-sum';
export type Variant = 'slow' | 'fast';

export interface DeclareChoice {
  id: string;
  label: string;
}

export interface VariantDef {
  /** 凡例と表の列名。 */
  label: string;
  code: string;
  /** 見積もりと §5.3 の間引きに使う ops 式。 */
  ops: string;
}

/** 実測から予想の答えを読み取った結果。 */
export interface Reading {
  /** 実測に最も近い選択肢。 */
  choiceId: string;
  /** 実測の説明（「100 倍（N = 10,000: 49,995,000 回 → N = 100,000: 4,999,950,000 回）」）。 */
  detail: string;
}

export interface Experiment {
  id: ExperimentId;
  title: string;
  /** 何を解くか（1 文）。 */
  task: string;
  textbook: string[];
  slow: VariantDef;
  fast: VariantDef;
  question: string;
  choices: DeclareChoice[];
  /** 遅い書き方の実測から、予想と比べる値を読み取る。読み取れなければ null。 */
  read(slowPoints: readonly MeterPoint[]): Reading | null;
}

/** 3 実験で共通の N（labs.md「ケースの N」。起動時間用の N = 0 は送らず、10^3 を表用に置く）。 */
export const EXPERIMENT_SIZES: readonly number[] = [
  1_000, 10_000, 20_000, 50_000, 100_000, 200_000, 400_000, 1_000_000,
];

const HEADER = `// stdin: "N seed"（seed を省くと 1）
let p = readLine()!.split(separator: " ").map { Int($0)! }
let n = p[0]

// データの生成（遅い書き方と速い書き方で同じ）
struct Lcg {
    var x: Int
    mutating func next() -> Int {
        x = (1103515245 * x + 12345) % 2147483648
        return x
    }
}
var rng = Lcg(x: p.count > 1 ? p[1] : 1)
`;

const DUP_DATA = `var a = Array(1...n)
for i in stride(from: n - 1, to: 0, by: -1) {
    a.swapAt(i, rng.next() % (i + 1))
}
`;

const SEARCH_DATA = `let a = (0..<n).map { 2 * $0 }
var hit = 0
`;

const SUM_DATA = `var a = [Int](repeating: 0, count: n)
for i in 0..<n {
    a[i] = rng.next() % 100 + 1
}
var total = 0
`;

function code(data: string, solve: string): string {
  return `${HEADER}${data}
// 解く部分
${solve}`;
}

function pointAt(points: readonly MeterPoint[], n: number): MeterPoint | undefined {
  return points.find((p) => p.n === n);
}

/** 最も近い選択肢（値の常用対数での距離）。 */
export function nearestByLog<T extends { value: number }>(
  options: readonly T[],
  x: number,
): T | undefined {
  if (!(x > 0)) return undefined;
  let best: T | undefined;
  let bestDist = Infinity;
  for (const o of options) {
    const d = Math.abs(Math.log10(o.value) - Math.log10(x));
    if (d < bestDist) {
      best = o;
      bestDist = d;
    }
  }
  return best;
}

function ticksText(p: MeterPoint): string {
  return `${p.ticksLowerBound ? '≥ ' : ''}${groupDigits(p.ticks ?? 0)} 回`;
}

const RATIO_CHOICES = [
  { id: 'x10', label: '10 倍', value: 10 },
  { id: 'x100', label: '100 倍', value: 100 },
  { id: 'x1000', label: '1000 倍', value: 1000 },
];

const DIGIT_CHOICES = [
  { id: 'e5', label: '10^5 前後', value: 1e5 },
  { id: 'e7', label: '10^7 前後', value: 1e7 },
  { id: 'e9', label: '10^9 前後', value: 1e9 },
  { id: 'e10', label: '10^10 前後', value: 1e10 },
];

export const EXPERIMENTS: readonly Experiment[] = [
  {
    id: 'duplicate',
    title: '重複があるか',
    task: '1 から N までを混ぜた列に、同じ値が 2 回出てくるかを調べます（答えは false）。',
    textbook: ['/compute/02-nested-loop/', '/kyopro/03-dict-set/'],
    slow: {
      label: '2 重ループ',
      ops: 'N*(N-1)/2',
      code: code(
        DUP_DATA,
        `var dup = false
outer: for i in 0..<n {
    for j in (i + 1)..<n {
        viz.tick()
        if a[i] == a[j] {
            dup = true
            break outer
        }
    }
}
print(dup)
`,
      ),
    },
    fast: {
      label: 'Set',
      ops: 'N',
      code: code(
        DUP_DATA,
        `var seen = Set<Int>()
var dup = false
for v in a {
    viz.tick()
    if !seen.insert(v).inserted {
        dup = true
        break
    }
}
print(dup)
`,
      ),
    },
    question: 'N を 10^4 から 10^5 にすると、2 重ループのステップ数は何倍になりますか。',
    choices: RATIO_CHOICES.map(({ id, label }) => ({ id, label })),
    read(points) {
      const a = pointAt(points, 10_000);
      const b = pointAt(points, 100_000);
      if (!a?.ticks || !b?.ticks) return null;
      const ratio = b.ticks / a.ticks;
      const c = nearestByLog(RATIO_CHOICES, ratio);
      if (!c) return null;
      return {
        choiceId: c.id,
        detail: `${b.ticksLowerBound ? '≥ ' : ''}${Math.round(ratio).toLocaleString('en-US')} 倍（N = 10,000: ${ticksText(a)} → N = 100,000: ${ticksText(b)}）`,
      };
    },
  },
  {
    id: 'search',
    title: '探す',
    task: '0, 2, 4, … と並んだ N 個の列から、N 個の値をそれぞれ探し、見つかった数を出します。',
    textbook: ['/algo/01-full-search/', '/algo/02-binary-search/'],
    slow: {
      label: '前から順に',
      ops: 'N^2*3/4',
      code: code(
        SEARCH_DATA,
        `for _ in 0..<n {
    let q = rng.next() % (2 * n + 1)
    for v in a {
        viz.tick()
        if v == q {
            hit += 1
            break
        }
    }
}
print(hit)
`,
      ),
    },
    fast: {
      label: '二分探索',
      ops: 'N*log2(N)',
      code: code(
        SEARCH_DATA,
        `for _ in 0..<n {
    let q = rng.next() % (2 * n + 1)
    var ng = -1
    var ok = a.count
    while ok - ng > 1 {
        let mid = (ng + ok) / 2
        viz.tick()
        if a[mid] >= q {
            ok = mid
        } else {
            ng = mid
        }
    }
    if ok < a.count && a[ok] == q {
        hit += 1
    }
}
print(hit)
`,
      ),
    },
    question: 'N = 10^5 のとき、前から順に探す書き方のステップ数はどのくらいですか。',
    choices: DIGIT_CHOICES.map(({ id, label }) => ({ id, label })),
    read(points) {
      const p = pointAt(points, 100_000);
      if (!p?.ticks) return null;
      const c = nearestByLog(DIGIT_CHOICES, p.ticks);
      if (!c) return null;
      return {
        choiceId: c.id,
        detail: `N = 100,000 で ${ticksText(p)}（${formatCount(p.ticks)}）`,
      };
    },
  },
  {
    id: 'range-sum',
    title: '区間の和',
    task: 'N 個の数の列について、区間の和を N 回求め、その合計を出します。',
    textbook: ['/algo/04-cumsum/'],
    slow: {
      label: '毎回足す',
      ops: 'N^2/3',
      code: code(
        SUM_DATA,
        `for _ in 0..<n {
    var l = rng.next() % n
    var r = rng.next() % n
    if l > r {
        swap(&l, &r)
    }
    r += 1
    for i in l..<r {
        viz.tick()
        total += a[i]
    }
}
print(total)
`,
      ),
    },
    fast: {
      label: '累積和',
      ops: '2*N',
      code: code(
        SUM_DATA,
        `var prefix = [Int](repeating: 0, count: n + 1)
for i in 0..<n {
    viz.tick()
    prefix[i + 1] = prefix[i] + a[i]
}
for _ in 0..<n {
    var l = rng.next() % n
    var r = rng.next() % n
    if l > r {
        swap(&l, &r)
    }
    r += 1
    viz.tick()
    total += prefix[r] - prefix[l]
}
print(total)
`,
      ),
    },
    question: 'N = 2×10^5 のとき、毎回足す書き方は 1 ケース 3 秒の制限に収まりますか。',
    choices: [
      { id: 'yes', label: '収まる' },
      { id: 'no', label: '収まらない' },
    ],
    read(points) {
      const p = pointAt(points, 200_000);
      // 実行時エラーの点の時間は参考値にならないので読まない（§6.1）
      if (!p || (!p.timedOut && p.exitCode !== 0)) return null;
      const over = p.timedOut;
      return {
        choiceId: over ? 'no' : 'yes',
        detail: over
          ? `N = 200,000 は 3 秒以内に終わりませんでした（${ticksText(p)}）`
          : `N = 200,000 は ${p.durationMs.toLocaleString('en-US')} ms で終わりました（${ticksText(p)}）`,
      };
    },
  },
];

export function experimentById(id: ExperimentId): Experiment {
  const e = EXPERIMENTS.find((x) => x.id === id);
  if (!e) throw new Error(`unknown experiment ${id}`);
  return e;
}

/** 実験の 1 版を計測するときの meter 指定。 */
export function variantMeter(v: VariantDef): MeterSpec {
  return { input: { kind: 'n-only' }, sizes: [...EXPERIMENT_SIZES], ops: v.ops };
}

/** ops 式の見積もり回数（表の「見積もり」列）。評価できなければ null。 */
export function predictedOps(ops: string, n: number): number | null {
  try {
    const v = evalOps(ops, { N: n });
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export interface DeviationEntry {
  experimentId: ExperimentId;
  title: string;
  question: string;
  declared: DeclareChoice;
  measured: DeclareChoice;
  detail: string;
  match: boolean;
}

/** 予想と実測の差（ずれ）の 1 件を作る。 */
export function deviation(
  e: Experiment,
  declaredId: string,
  slowPoints: readonly MeterPoint[],
): DeviationEntry | null {
  const reading = e.read(slowPoints);
  const declared = e.choices.find((c) => c.id === declaredId);
  const measured = reading ? e.choices.find((c) => c.id === reading.choiceId) : undefined;
  if (!reading || !declared || !measured) return null;
  return {
    experimentId: e.id,
    title: e.title,
    question: e.question,
    declared,
    measured,
    detail: reading.detail,
    match: declared.id === measured.id,
  };
}
